import { useState } from "react";
import API from "../../api/api";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toolbarOptions } from "../editorToolbar";
import DashboardLayout from "../dashboard/DashboardLayout";

export default function CreateArticle() {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState("");
  const [featuredImage, setFeaturedImage] = useState("");
  const [imageCredit, setImageCredit] = useState(""); // ✅ IMAGE CREDIT
  const [seoTitle, setSeoTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [userId, setUserId] = useState("");
  const [isLive, setIsLive] = useState(false);


  const modules = { toolbar: toolbarOptions };

  const formats = [
    "header", "bold", "italic", "underline", "strike",
    "color", "background", "list", "bullet", "align",
    "blockquote", "code-block", "link", "image", "video"
  ];

  // ==========================
  // 🔍 PREVIEW FUNCTION
  // ==========================
  const preview = () => {
    if (!title || !content) {
      alert("Title and content are required for preview");
      return;
    }

    const previewData = {
      title,
      content,
      category,
      tags: tags ? tags.split(",").map(t => t.trim()) : [],
      images: featuredImage,
      image_credit: imageCredit,
      seo_title: seoTitle || title,
      meta_description: metaDescription,
      author: {
        name: "Admin (Preview)",
        image: "https://via.placeholder.com/120",
        bio: "This is preview mode. Article is not published yet.",
      },
      created_at: new Date().toISOString(),
      views: 0,
      reactions: {},
    };

    localStorage.setItem("article_preview", JSON.stringify(previewData));
    window.open("/article/preview", "_blank");
  };


  // ==========================
  // 🚀 PUBLISH FUNCTION
  // ==========================
  const submit = async () => {
    if (!title || !content || !category || !userId) {
      alert("Please fill all required fields");
      return;
    }

    try {
      const payload = {
        title,
        content,
        category,
        tags: tags ? tags.split(",").map(t => t.trim()) : [],
        images: featuredImage || "",
        image_credit: imageCredit,
        seo_title: seoTitle || title,
        meta_description: metaDescription || "",
        user_id: Number(userId),
        is_live: isLive || false,

      };
      console.log(payload, 'isLive');

      await API.post("/articles/", payload);
      alert("Article Published 🎉");
    } catch (err) {
      console.error("Submit failed:", err.response?.data || err.message);
      alert("Failed to publish article");
    }
  };


  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-6 py-10 bg-gray-50">

        {/* PAGE HEADER */}
        <div className="mb-8 border-l-4 border-red-600 pl-4">
          <h1 className="text-4xl font-bold text-gray-900">
            Create New Article
          </h1>
          <p className="text-gray-600 mt-1">
            Write, optimize, and publish your content
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* LEFT: EDITOR */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow border border-red-200">

            <div className="p-6 border-b border-red-200">
              <input
                className="theme-border p-2 rounded w-full"
                placeholder="Article title..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="p-6">
              <ReactQuill
                value={content}
                onChange={setContent}
                modules={modules}
                formats={formats}
                theme="snow"
                className="min-h-[350px]"
              />
            </div>
          </div>

          {/* RIGHT: SETTINGS */}
          <div className="space-y-6 sticky top-24 h-fit">

            {/* ACTION BUTTONS */}
            <div className="bg-white rounded-xl shadow border border-red-300 p-5 space-y-3">
              <button
                onClick={preview}
                className="w-full py-3 rounded-lg font-semibold
                           text-red-600 border border-red-500
                           hover:bg-red-50 transition"
              >
                👁 Preview Article
              </button>

              <button
                onClick={submit}
                className="w-full py-3 rounded-lg font-semibold
                           text-white bg-red-600 hover:bg-red-700 transition"
              >
                🚀 Publish Article
              </button>
            </div>

            <div className="bg-white rounded-xl shadow border border-red-200 p-5 space-y-4">
              <h3 className="font-semibold text-lg text-red-600">
                Article Settings
              </h3>

              <select
                className="w-full border border-red-300 rounded p-2"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">Select Category</option>
                <option value="Programming">Programming</option>
                <option value="AI">AI</option>
                <option value="DevOps">DevOps</option>
                <option value="Startup Business">Startup Business</option>
                <option value="Technology">Technology</option>
                <option value="CyberSecurity">CyberSecurity</option>
                <option value="Science">Science</option>
              </select>

              <input
                className="theme-border p-2 rounded"
                placeholder="Tags (comma separated)"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
              />

              <input
                className="theme-border p-2 rounded"
                placeholder="Featured Image (CDN URL)"
                value={featuredImage}
                onChange={(e) => setFeaturedImage(e.target.value)}
              />
              {/* ✅ IMAGE CREDIT INPUT (VISIBLE) */}

              <input
                className="theme-border p-2 rounded"
                placeholder="e.g. Image credit"
                value={imageCredit}
                onChange={(e) => setImageCredit(e.target.value)}
              />

              <input
                className="theme-border p-2 rounded"
                placeholder="Author User ID"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
              />
            </div>
            {/* ================= LIVE STATUS TOGGLE ================= */}
            <div className="bg-white rounded-xl shadow border border-red-200 p-5">
              <h3 className="font-semibold text-lg text-red-600 mb-3">
                Article Status
              </h3>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-800">
                    {isLive ? "Live Article" : "Draft Mode"}
                  </p>
                  <p className="text-sm text-gray-500">
                    {isLive
                      ? "Article will be visible to users"
                      : "Article will be saved as draft"}
                  </p>
                </div>

                {/* TOGGLE */}
                <button
                  onClick={() => setIsLive(!isLive)}
                  className={`
        relative inline-flex h-7 w-14 items-center rounded-full
        transition-colors duration-300
        ${isLive ? "bg-red-600" : "bg-gray-300"}
      `}
                >
                  <span
                    className={`
          inline-block h-5 w-5 transform rounded-full bg-white
          transition-transform duration-300
          ${isLive ? "translate-x-7" : "translate-x-1"}
        `}
                  />
                </button>
              </div>
            </div>


            <div className="bg-white rounded-xl shadow border border-red-200 p-5 space-y-4">
              <h3 className="font-semibold text-lg text-red-600">
                SEO Settings
              </h3>

              <input
                className="theme-border p-2 rounded"
                placeholder="SEO Title"
                value={seoTitle}
                onChange={(e) => setSeoTitle(e.target.value)}
              />

              <textarea
                className="w-full border border-red-300 rounded p-2"
                placeholder="Meta Description"
                rows={3}
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
              />
            </div>

          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}


